import { RadioGroup, Switch } from "@aidkitorg/component-library";
import { RadioGroupOption } from "@aidkitorg/component-library/src/RadioGroup";
import { Block, ConditionalContentList, DefinitionComponent, ExpandedSurvey, RichText, Select, Survey, Text } from "@aidkitorg/types/lib/survey";
import {
  encodeWaypoint,
  getComponentIdentifier,
  getComponentNameTitleOrPath,
  WAYPOINT_DELIMITER as DELIMITER,
  WAYPOINT_POSITIONAL_PREFIX as POSITIONAL_PREFIX,
} from "@aidkitorg/types/lib/translation/componentIdentifier";
import { DistroComponent } from "@aidkitorg/types/lib/translation/questions";
import { deepCopy, extractDefinitions, generateUuid } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import { applyTemplateSubstitutions, resolveTemplatedBlockIterations } from "@aidkitorg/types/lib/translation/templates";
import { FIELD_REF_REGEX } from "@aidkitorg/types/lib/util";
import { toName } from '@aidkitorg/typesheets/lib/utils';
import { Dialog, Transition } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { captureException } from "@sentry/react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { SearchableComponent } from "./Config/Util";
import { BUTTON_CLASS } from "./Util";
import { EditorLocation, DefinitionMatch, findDefinitionUses, isDefinitionReference, readDefinition } from './DefinitionSearch';
import { goToEditorLocation } from '@aidkitorg/typesheets/lib/editorNavigation';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

/**
 * Returns a component's ordered child components exactly as the editor renders them — so that
 * search tree-building (findComponent), field-mismatch tree-building (buildPathMap), and scroll-to
 * path-resolution (getComponentsInPath) all descend the survey identically. Order is significant:
 * for Conditional Blocks it is `components` then `otherwise`, matching the editor DOM, so a
 * positional (index-based) waypoint lines up with the rendered child wrappers.
 *
 * Returns null for components that are not descendable containers (leaf blocks, and single-object
 * descents like Third Party Check's `type` / Custom Query's `visualization`, which findComponent
 * still searches separately but scroll-to cannot precisely reach).
 */
export function getChildComponents(component: SearchableComponent): SearchableComponent[] | null {
  switch (component?.kind) {
    case 'Section':
    case 'Collection':
    case 'Dashboard':
    case 'Dashboard Section':
      return Array.isArray(component.components) ? component.components : null;
    case 'Subsurvey':
      return Array.isArray(component.sections) ? component.sections : null;
    case 'Templated Block':
      return Array.isArray(component.components) ? component.components : null;
    case 'Conditional Block':
      return [...(component.components || []), ...(component.otherwise || [])];
    case 'Definition Bucket':
      return Array.isArray(component.components) ? component.components : null;
    // Definitions use property-path navigation; findDefinitionUses searches their contents.
    default:
      return null;
  }
}

export type MatchType = "Name/Title/Path" | "Target Field" | "Content" | "Formula" | "Kind" | "Comments" | "Other";

/** A piece of a component's text that search tests against, tagged with the bucket it matched as. */
export type MatchableText = { text: string, matchType: MatchType };

export type TreeBlock = {
  thing: unknown,
  tree: string[],
  matchTypes: MatchType[],
  tab?: string,
  /** The Definition this Reference points to. */
  definition?: DefinitionComponent,
  location?: EditorLocation,
  definitionLocation?: EditorLocation,
  definitionMatches?: DefinitionMatch[],
  invalidReference?: boolean,
};

type SearchOptions = {
  matchCase: boolean,
  wholeMatchOnly: boolean,
  includeDashboards: boolean,
  includeComments: boolean,
  searchAllAttributes: boolean,
}

export function highlightSearchTerm(jsonString: string, searchTerm: string | string[], searchOptions: SearchOptions) {
  let segments: string[] = [];
  const isArrayOfTerms = Array.isArray(searchTerm);
  try {
    let searchTermRegex;
    if (searchOptions.wholeMatchOnly) {
      // Escape special characters in the searchTerm to be used in a regular expression
      if (isArrayOfTerms) {
        const escapedSearchTerms = searchTerm.map(term => term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
        // Create a regular expression with word boundaries to look for instances of the searchTerm token
        searchTermRegex = new RegExp(`\\b(${escapedSearchTerms.join('|')})\\b`, 'gi');
      } else {
        const escapedSearchTerm = searchTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        // Create a regular expression with word boundaries to look for instances of the searchTerm token
        searchTermRegex = new RegExp(`\\b(${escapedSearchTerm})\\b`, 'gi');
      }
    } else {
      searchTermRegex = new RegExp(`(${isArrayOfTerms ? searchTerm.join('|') : searchTerm})`, 'gi');
    }
    segments = jsonString.split(searchTermRegex);
  } catch (_) {
    // Do nothing for now
  }
  return segments.map((segment, index) => {
    if ((searchOptions.matchCase && (isArrayOfTerms ? searchTerm.some(term => term === segment) : segment === searchTerm))
      || (!searchOptions.matchCase && (isArrayOfTerms ? searchTerm.some(term => term.toLowerCase().includes(segment.toLowerCase())) : segment.toLowerCase() === searchTerm.toLowerCase()))) {
      return (
        <span key={index} style={{ backgroundColor: 'moccasin', fontWeight: 'bold' }}>
          {segment}
        </span>
      );
    }
    return <span key={index}>{segment}</span>;
  });
}




/**
 * Walks a RichText | ConditionalContentList (mirrors addMessageText's branch shape below),
 * concatenating every `en` string found across every branch. For search matching, where finding
 * more text than strictly necessary is never wrong — a term buried in any conditional branch
 * should still be found.
 */
export function flattenContentText(content?: RichText | ConditionalContentList): string {
  if (!content) return '';
  if ('en' in content) return content.en || '';
  return content.map(part => {
    if ('en' in part) return part.en || '';
    return [flattenContentText(part.components), part.otherwise ? flattenContentText(part.otherwise) : '']
      .filter(Boolean).join(' ');
  }).filter(Boolean).join(' ');
}

/**
 * Walks a RichText | ConditionalContentList depth-first, returning just the FIRST non-empty `en`
 * text found. Used to build a DOM attribute-selector clause, which needs a single string that
 * exactly equals some one descendant's rendered `data-en` value: the editor only ever stamps one
 * branch's own `en` value per DOM node, never a concatenation across branches, so
 * flattenContentText's output would never match anything there.
 */
export function firstContentText(content?: RichText | ConditionalContentList): string {
  if (!content) return '';
  if ('en' in content) return content.en || '';
  for (const part of content) {
    if ('en' in part) {
      if (part.en) return part.en;
      continue;
    }
    const fromComponents = firstContentText(part.components);
    if (fromComponents) return fromComponents;
    if (part.otherwise) {
      const fromOtherwise = firstContentText(part.otherwise);
      if (fromOtherwise) return fromOtherwise;
    }
  }
  return '';
}

/** Searchable text and categories for one component. Callers handle its children. */
export function collectMatchableText(
  component: SearchableComponent,
  options: { includeComments: boolean }
): MatchableText[] {
  const thing = component as any;
  const collected: MatchableText[] = [];
  const add = (text: string, matchType: MatchType) => { collected.push({ text, matchType }); };

  add(getComponentNameTitleOrPath(component), "Name/Title/Path");
  add(thing.targetField || '', "Target Field");
  add(flattenContentText(thing.content), "Content");

  ['formula', 'condition', 'filter', 'sql', 'groups', 'columns'].forEach(key => {
    const unparsedFormula = thing[key] || '';
    add(typeof unparsedFormula === 'object' ? JSON.stringify(unparsedFormula) : unparsedFormula, "Formula");
  });

  add(thing.kind || '', "Kind");
  if (thing.kind === 'Definition') add(thing.label || '', 'Name/Title/Path');
  // Template substitutions also have an untagged { key, value } form.
  if (typeof thing.key === 'string' && typeof thing.value === 'string') {
    add(thing.key, 'Content');
    add(thing.value, 'Content');
  }

  if (options.includeComments) {
    add(thing["//"] || '', "Comments");
  }

  if (component.kind === 'Select') {
    add(component.choices ? JSON.stringify(component.choices) : '', "Content");
  }

  if (component.kind === 'Action') {
    if (component.fields) {
      for (let field of component.fields) {
        add(field, "Target Field");
      }
    }

    if (component.action && typeof component.action !== 'string' && (component.action as any).targetField) {
      add((component.action as any).targetField, "Target Field");
    }
    if (component.explanation) {
      add(component.explanation.en || '', "Content");
    }
  }

  if (component.kind === 'Payment') {
    add(component.cardIdField || '', 'Target Field');
    add((typeof component.ledger === 'string' ? component.ledger : component.ledger?.field) || '', 'Content');
    add(component.targetField || '', 'Target Field');
    add(component.amount || '', 'Content');
    add(component.type || '', 'Content');
  }

  function addMessageText(message: RichText | ConditionalContentList) {
    if ('en' in message) {
      add(message.en, 'Content');
      return;
    }
    for (const part of message) {
      if ('en' in part) {
        add(part.en || '', 'Content');
      } else {
        addMessageText(part.components);
        if (part.otherwise) addMessageText(part.otherwise);
      }
    }
  }

  if (component.kind === 'Notification' || component.kind === 'InlineNotification') {
    add(component.targetPrefix || '', 'Target Field');
    add(JSON.stringify(component.initial_notification.enabled_when || ''), "Formula");

    addMessageText(component.initial_notification.message);

    add(component.initial_notification.email_subject.en || '', 'Content');

    if (component.initial_notification.email_message) {
      addMessageText(component.initial_notification.email_message);
    }

    if (component.kind === 'Notification' && component.followups) {
      component.followups.forEach((followup) => {
        add(followup.suffix || '', 'Target Field');
        add(followup.email_subject.en || '', 'Content');
        add(JSON.stringify(followup.send_if || ''), "Formula");
        addMessageText(followup.message);
        if (followup.email_message) {
          addMessageText(followup.email_message);
        }
      });
    }
  }

  return collected;
}

/** Read the ID from either Definition format. */
export function definitionIdOf(component: SearchableComponent): string | undefined {
  const thing = component as any;
  if (thing?.kind === 'Definition') return thing.id;
  return thing?.definitionProps?.kind === 'Definition' ? thing.definitionProps.id : undefined;
}

export async function findComponent(expandedSurvey: ExpandedSurvey, searchTerm: string, options: SearchOptions): Promise<TreeBlock[] | null> {
  if (!expandedSurvey || !searchTerm) return null;

  if (!options.matchCase) searchTerm = searchTerm.toLowerCase();

  // Escape special characters in the searchTerm to be used in a regular expression
  const escapedSearchTerm = searchTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  // Create a regular expression with word boundaries to look for instances of the searchTerm token
  const tokenMatchRegex = new RegExp(`\\b${escapedSearchTerm}\\b`);

  const matches = (text: string) => {
    const value = options.matchCase ? text : text.toLowerCase();
    return options.wholeMatchOnly ? tokenMatchRegex.test(value) : value.includes(searchTerm);
  };

  // Walk the non-templated-expanded survey — this is what Distro renders, so every match maps to a
  // walkable location for scroll-to. Templated Blocks are left intact here and expanded
  // locally, per iteration, only to match/display their substituted values (see the block case in
  // traverse). Navigation waypoints stay un-substituted, matching the editor DOM.
  const survey = deepCopy(expandedSurvey.survey) as Survey;
  // Resolve template iterations and substitutions that use Definitions.
  const definitionsById = expandedSurvey?.definitions ? extractDefinitions(expandedSurvey.definitions) : undefined;


  // This is a set of JSON Stringified TreeBlocks
  const foundThings = new Set<string>();

  function traverse(
    rawThing: SearchableComponent,
    thing: SearchableComponent,
    parentTree: string[],
    isDashboardComponent?: boolean,
    tab?: string,
    indexInParent: number = 0
  ): Block[] {
    if (isDashboardComponent && !options.includeDashboards) return [];

    // `rawThing` is the un-substituted component the editor renders (placeholders like
    // `payment_<<num>>`) — navigation/identity uses it so paths match the DOM. `thing` is its
    // substituted counterpart, used for match-testing and the displayed result. The two are walked
    // in lockstep (structurally identical); `thing === rawThing` wherever no substitution applies.
    const tree = [...parentTree, encodeWaypoint(rawThing, indexInParent)];
    const thingToAdd: TreeBlock = { thing, tree, matchTypes: [], tab };

    const maybeAdd = (possibleMatch: string, matchType: TreeBlock['matchTypes'][number]) => {
      if (matches(possibleMatch)) {
        // Show each result once per category.
        if (!thingToAdd.matchTypes.includes(matchType)) thingToAdd.matchTypes.push(matchType);
      }
    }

    collectMatchableText(thing, options).forEach(({ text, matchType }) => maybeAdd(text, matchType));

    if (options.searchAllAttributes) {
      const walkJson = (node: unknown) => {
        if (typeof node === 'string' || typeof node === 'number') {
          maybeAdd(String(node), "Other");
        } else if (Array.isArray(node)) {
          node.forEach(walkJson);
        } else if (node !== null && typeof node === 'object') {
          Object.values(node as Record<string, unknown>).forEach(walkJson);
        }
      }
      walkJson(thing);
    }

    if (thingToAdd.matchTypes.length && !isDefinitionReference(thing) && !definitionIdOf(thing)) {
      const toAdd = JSON.stringify(thingToAdd);
      foundThings.add(toAdd);
    }

    // Recurse. `rawThing` supplies the un-substituted structure/identity (waypoints); `thing` supplies
    // the substituted view (match/display). They are structurally identical, so we descend them in
    // lockstep. Substitution is applied ONCE per component per iteration (in the block case below) —
    // never re-applied as we go deeper, since each child's substituted subtree is passed straight in.
    const rawChildren = getChildComponents(rawThing);

    if (rawThing.kind === 'Templated Block') {
      // Resolve iterations from `thing` so any enclosing block's substitutions — already baked into
      // thing.iterations — carry through. For each iteration, substitute each child exactly once.
      const iterations = resolveTemplatedBlockIterations(rawThing.iterations, definitionsById);
      const subChildren = getChildComponents(thing) || [];
      for (const iterSubs of iterations) {
        (rawChildren || []).forEach((rawChild, i) => {
          const subChild = applyTemplateSubstitutions(subChildren[i] ?? rawChild, iterSubs);
          traverse(rawChild, subChild, tree, isDashboardComponent, tab, i);
        });
      }
      return [];
    }

    // Single-object descents the editor does not render as a walkable child-list — searched for
    // matches, but not precisely reachable by scroll-to (getChildComponents returns null for these).
    if (rawThing.kind === 'Third Party Check') {
      traverse(rawThing.type, rawThing.type, tree, isDashboardComponent, tab, 0);
    }
    if (rawThing.kind === 'Custom Query') {
      traverse(rawThing.visualization, rawThing.visualization, tree, true, tab, 0);
    }

    if (rawChildren) {
      const subChildren = getChildComponents(thing) || rawChildren;
      const childrenAreDashboard = isDashboardComponent || rawThing.kind === 'Dashboard' || rawThing.kind === 'Dashboard Section';
      rawChildren.forEach((rawChild, i) => traverse(rawChild, subChildren[i] ?? rawChild, tree, childrenAreDashboard, tab, i));
    }
    return [];
  }
  (survey || []).forEach((component: SearchableComponent, i: number) => {
    // At the top level nothing is substituted yet, so raw and substituted views are the same object.
    traverse(component, component, [], false, 'survey', i);
  });

  // Search Definition Buckets here; findDefinitionUses handles individual Definitions.
  (expandedSurvey.definitions || []).forEach((definition, i) => {
    traverse(definition, definition, [], false, 'definitions', i);
  });

  // If searchAllAttributes is enabled and we have an ExpandedSurvey, search other tabs
  // using a loose JSON walk so we don't need every tab type to be a SearchableComponent.
  if (options.searchAllAttributes && expandedSurvey) {
    const walkJson = (node: unknown, onMatch: () => void) => {
      if (typeof node === 'string' || typeof node === 'number') {
        if (matches(String(node))) onMatch();
      } else if (Array.isArray(node)) {
        node.forEach(child => walkJson(child, onMatch));
      } else if (node !== null && typeof node === 'object') {
        Object.values(node).forEach(child => walkJson(child, onMatch));
      }
    };

    for (const key of (Object.keys(expandedSurvey) as (keyof ExpandedSurvey)[])) {
      // These tabs already have individual search results.
      if (key === 'survey' || key === 'definitions') continue;
      const tabContent = expandedSurvey[key];
      if (tabContent === null || tabContent === undefined) continue;
      const items: unknown[] = Array.isArray(tabContent) ? tabContent : [tabContent];
      for (const item of items) {
        let matched = false;
        walkJson(item, () => { matched = true; });
        if (matched) {
          const thingToAdd: TreeBlock = { thing: item, tree: [key], matchTypes: ['Other'], tab: key };
          foundThings.add(JSON.stringify(thingToAdd));
        }
      }
    }
  }

  let things: TreeBlock[] = [];
  foundThings.forEach(thing => { things.push(JSON.parse(thing) as TreeBlock); });

  const matchingText = (node: any): MatchableText[] => {
    // Template key/value entries may omit kind.
    const text = node.kind || (typeof node.key === 'string' && typeof node.value === 'string')
      ? collectMatchableText(node, options) : [];
    if (options.searchAllAttributes) {
      // Also search nested values on the Reference itself.
      const addValue = (value: unknown): void => {
        if (typeof value === 'string' || typeof value === 'number') {
          text.push({ text: String(value), matchType: 'Other' });
        } else if (isDefinitionReference(node) && value && typeof value === 'object') {
          Object.values(value).forEach(addValue);
        }
      };
      Object.values(node).forEach(addValue);
    }
    return text.filter(m => matches(m.text));
  };
  things.push(...findDefinitionUses(expandedSurvey, matchingText, options.includeDashboards));

  // Rank the components by how close they are to the search term
  things.sort((a, b) => {
    let aName = ((a.thing as any).name?.en || '').toLowerCase();
    let bName = ((b.thing as any).name?.en || '').toLowerCase();
    let aKey = ((a.thing as any).targetField || '').toLowerCase();
    let bKey = ((b.thing as any).targetField || '').toLowerCase();

    // console.log("Comparing", aName, bName, aKey, bKey, name);

    if (!aName && !aKey) return 1;
    if (!bName && !bKey) return -1;

    // Rank ascending top score = BOTH Name and Key include
    // Rank descending top score = EITHER Name or Key include
    if (aKey.startsWith(searchTerm) && bKey.startsWith(searchTerm)) return 0;
    if (aName === searchTerm || aKey === searchTerm || aKey.startsWith(searchTerm)) return -1;
    if (bName === searchTerm || bKey === searchTerm || bKey.startsWith(searchTerm)) return 1;
    if (aName.includes(searchTerm) && aKey.includes(searchTerm) && bName.includes(searchTerm) && bKey.includes(searchTerm)) return 0;
    if (aName.includes(searchTerm) && aKey.includes(searchTerm)) return -1;
    if (bName.includes(searchTerm) && bKey.includes(searchTerm)) return 1;
    if (aName.includes(searchTerm) || aKey.includes(searchTerm)) return -1;
    if (bName.includes(searchTerm) || bKey.includes(searchTerm)) return 1;
    return 0;
  });
  return things;
}

// Note: we could do away with this function if we decide we're okay keeping many references
// to parts of the survey in the return object of findComponent(), i.e. instead of the path as
// array of strings, we return an array of arrays of components along the path to the leaf component.
export function getComponentsInPath(survey: SearchableComponent[], path: string[]): SearchableComponent[] {

  const foundComponents: SearchableComponent[] = [];

  const recursiveSearch = (component: SearchableComponent, currentDepth: number, indexInParent: number): boolean => {
    if (currentDepth >= path.length) {
      return false;
    }

    const [kind, ...rest] = path[currentDepth]?.split(DELIMITER) || [path[currentDepth], []];
    const identifier = rest.length > 0 ? rest.join(DELIMITER) : '';

    // All components have a kind.
    if (!kind || kind !== component.kind) {
      return false;
    }

    const isPositional = identifier.startsWith(POSITIONAL_PREFIX);
    if (isPositional) {
      // Identifier-less waypoint (Conditional Block): the only disambiguator is its position among
      // its parent's children (see getChildComponents ordering), so it must sit at that exact index.
      if (parseInt(identifier.slice(POSITIONAL_PREFIX.length), 10) !== indexInParent) {
        return false;
      }
    } else if (identifier && identifier !== getComponentIdentifier(component)) {
      return false;
    }

    // Record how the DOM walk should select this component: by index for positional waypoints,
    // otherwise by its identifier (attribute selector). This is stamped on findAndGoToComponent's
    // private survey copy, never on the objects shown in search results.
    const markOnPath = () => {
      (component as any).__scrollChildIndex = isPositional ? indexInParent : undefined;
      foundComponents.push(component);
      return true;
    };

    if (currentDepth === path.length - 1) {
      return markOnPath();
    }

    const children = getChildComponents(component);
    if (!children) {
      return false;
    }

    for (let i = 0; i < children.length; i++) {
      if (recursiveSearch(children[i], currentDepth + 1, i)) {
        return markOnPath();
      }
    }

    return false;
  }

  for (let i = 0; i < survey.length; i++) {
    if (recursiveSearch(survey[i], 0, i)) {
      break;
    }
  }

  return foundComponents.reverse();
}

/**
 * Locates the desired leaf node within the Distro Configuration.
 * Starts with the topmost node in the path, and proceeds one level of
 * depth at a time, expanding nodes using the [show] buttons until the leaf is reached.
 * it then scrolls to the leaf!
 *
 * `editorRoot` is the shadow root of the editor to walk. ConfigPage keeps one `<distro-editor>` per
 * visited tab (inactive ones stay in the DOM, hidden), so callers that know which editor is active
 * should pass it; without it we fall back to the first `<distro-editor>` in the document.
 *
 * Resolves true if the target was found and scrolled to, false otherwise.
 */
export async function findAndGoToComponent(tree: string[], roots: SearchableComponent[], tab?: string, editorRoot?: ShadowRoot | null): Promise<boolean> {
  // Walk the UN-EXPANDED tree: it is exactly what the editor renders, so the path resolved from a
  // (likewise un-expanded) search result lines up with the DOM. Templated Blocks appear once, via
  // their id; Conditional Blocks are located positionally (see __scrollChildIndex below).
  console.log('[scroll-to] locating component details', tree);
  const surveyCopy = deepCopy(roots);
  const components = getComponentsInPath(surveyCopy, tree);
  console.log('[scroll-to] components found', components);

  const root = editorRoot ?? document.getElementsByTagName('distro-editor')[0].shadowRoot!;

  // Navigate to the appropriate tab (default to survey if not specified)
  const tabName = tab || 'survey';
  const targetTab = root.querySelector(`[data-tab-name=${tabName}]`);

  if (targetTab) targetTab.dispatchEvent(new Event('click', { bubbles: true }));
  await new Promise(r => setTimeout(r, 50));

  const collectMatches = (scope: Element | ShadowRoot, selector: string) => {
    const matches = new Set<Element>(Array.from(scope.querySelectorAll(selector)));
    if (scope instanceof Element && scope.matches(selector)) {
      matches.add(scope);
    }
    return matches;
  };

  const getInnermostMatches = (elementSet: ReadonlySet<Element>) => {
    const nonInnermost = new Set<Element>();
    const elements = Array.from(elementSet);

    for (const element of elements) {
      let ancestor = element.parentElement;
      while (ancestor) {
        if (elementSet.has(ancestor)) {
          nonInnermost.add(ancestor);
        }
        ancestor = ancestor.parentElement;
      }
    }

    return elements.filter(element => !nonInnermost.has(element));
  };

  const buildSelectorClauses = (component: SearchableComponent) => {
    const clauses: string[] = [];
    const pushClause = (key: string, value?: string) => {
      if (!value) return;
      clauses.push(`[${toName(key)}="${CSS.escape(value.substring(0, 100))}"]`);
    };

    pushClause('kind', component.kind);

    // Prefer stable identifiers that are consistently rendered in the editor DOM.
    pushClause('path', (component as any).path);
    pushClause('targetField', (component as any).targetField);
    pushClause('targetPrefix', (component as any).targetPrefix);
    pushClause('sectionId', (component as any).sectionId);
    // IDs keep navigation stable when labels change.
    if (component.kind === 'Templated Block' || component.kind === 'Definition') pushClause('id', (component as any).id);

    // Fall back to human-readable labels only when there isn't a stronger identifier.
    if (!clauses.some(clause => clause.includes('data-path') || clause.includes('data-targetfield') || clause.includes('data-targetprefix') || clause.includes('data-sectionid') || clause.includes('data-id'))) {
      pushClause('en', (component as any).name?.en || (component as any).title?.en);
      pushClause('en', firstContentText((component as any).content));
    }

    return clauses;
  };

  const buildSelector = (component: SearchableComponent) => {
    const selectorClauses = buildSelectorClauses(component);
    return '[data-tracker-type=object]:has' + selectorClauses
      .map(clause => `(${clause})`)
      .join(':has');
  };

  const waitForLayout = async () => {
    await new Promise(resolve => requestAnimationFrame(() => resolve(null)));
    await new Promise(resolve => requestAnimationFrame(() => resolve(null)));
  };

  let finalTarget: Element | null = null;

  const scrollFinalTargetIntoView = async (): Promise<boolean> => {
    await waitForLayout();
    await new Promise(resolve => setTimeout(resolve, 100));

    if (!finalTarget) {
      console.log('scroll-to final target was not captured during search');
      return false;
    }

    if (!finalTarget.isConnected) {
      console.log('scroll-to final target disconnected before final scroll');
      return false;
    }

    console.log('scroll-to final target after expansion', finalTarget);
    finalTarget.scrollIntoView({ block: 'center', behavior: 'smooth' });
    return true;
  };

  // Maps a container kind to the editor `data-btn-type`(s) of its child-list expander(s), in DOM
  // order. The child-list expander is NOT always the first one — Templated Blocks also expose
  // `iterations` and `id` expanders — so we must click the specific button(s) for the components
  // list, or descending goes down the wrong branch.
  const CHILD_LIST_BTN_TYPES: Record<string, string[]> = {
    'Section': ['components'],
    'Collection': ['_'],
    'Subsurvey': ['sections'],
    'Templated Block': ['_'],
    'Conditional Block': ['_', 'Otherwise'],
    'Dashboard': ['components'],
    'Dashboard Section': ['components'],
    'Definition Bucket': ['components'],
  };
  // Expanders that never lead to a walkable child-list (used as a denylist for unknown kinds).
  const NON_CHILD_LIST_BTN_TYPES = ['options', 'id', 'iterations', 'substitutions'];

  // True when `el`'s nearest enclosing component object is exactly `owner` — i.e. `el` belongs to
  // `owner` itself, not to one of its nested child components.
  const belongsTo = (el: Element, owner: Element) => {
    let a = el.parentElement;
    while (a && a !== owner) {
      if (a.getAttribute?.('data-tracker-type') === 'object') return false;
      a = a.parentElement;
    }
    return a === owner;
  };

  // Clicks the child-list expander(s) for `element` (a component object of the given kind) so its
  // children render. Uses the kind's known btn-type(s); for unknown kinds, clicks every expander
  // not on the denylist (preserving the previous best-effort behavior).
  const expandChildLists = (element: Element, kind: string) => {
    const wanted = CHILD_LIST_BTN_TYPES[kind];
    const expanders = Array.from(element.querySelectorAll('#expander')).filter(btn => {
      if (!belongsTo(btn, element)) return false;
      const t = btn.getAttribute('data-btn-type') || '';
      return wanted ? wanted.includes(t) : !NON_CHILD_LIST_BTN_TYPES.includes(t);
    });
    for (const btn of expanders) {
      if (btn.textContent?.includes('show')) {
        btn.dispatchEvent(new Event('click', { bubbles: true }));
      }
    }
  };

  // The rendered item wrappers of `element`'s child-list(s), concatenated in DOM order so their
  // indices line up with getChildComponents. Scopes to each list's own array container (found via
  // its expander) so sibling object props (e.g. a Conditional Block's `condition`) and nested arrays
  // are excluded. Assumes expandChildLists(element, kind) has already run.
  const getChildItemWrappers = (element: Element, kind: string): Element[] => {
    const wanted = CHILD_LIST_BTN_TYPES[kind];
    const wrappers: Element[] = [];
    for (const btnType of (wanted || [])) {
      const expander = Array.from(element.querySelectorAll('#expander')).find(btn =>
        belongsTo(btn, element) && (btn.getAttribute('data-btn-type') || '') === btnType);
      if (!expander) continue;
      // The expander and the prop's value subtree share the prop's `flex-col` wrapper.
      const flexCol = expander.closest('.flex-col');
      const valueSubtree = flexCol?.querySelector(':scope > div.w-full');
      // The array's own container is the first `.w-full.group` inside the value subtree.
      const container = valueSubtree?.querySelector('.w-full.group') || valueSubtree;
      if (!container) continue;
      // Direct-child item wrappers are those actually containing a component (skip add/paste "dot"
      // control rows, which contain no tracked node).
      for (const child of Array.from(container.children)) {
        if (child.querySelector('[data-tracker-type]')) wrappers.push(child);
      }
    }
    return wrappers;
  };

  // For a positional (Conditional Block) waypoint, selects the child at its recorded index within
  // the parent element's rendered child-list.
  const selectPositionalChild = (parentElement: Element | ShadowRoot, parentKind: string, index: number): Element | null => {
    if (parentElement instanceof Element) {
      const wrapper = getChildItemWrappers(parentElement, parentKind)[index];
      return wrapper?.querySelector('[data-tracker-type=object]') || null;
    }
    // Top-level (root) positional match: best-effort over the survey tab's outermost list container.
    const container = parentElement.querySelector('.w-full.group');
    if (!container) return null;
    const wrappers = Array.from(container.children).filter(c => c.querySelector('[data-tracker-type]'));
    return wrappers[index]?.querySelector('[data-tracker-type=object]') || null;
  };

  const searchComponent = async (depth: number, element: Element | ShadowRoot | null, parentKind: string | null) => {
    if (depth >= components.length) {
      console.log('scroll-to depth is greater than components length', depth, components.length);
      return;
    }

    const comp = components[depth];
    const scope = element || root;
    const positionalIndex = (comp as any).__scrollChildIndex;

    let candidates: Element[];
    if (typeof positionalIndex === 'number') {
      // Identifier-less (Conditional Block) waypoint — select by position within the parent's list.
      const picked = selectPositionalChild(scope, parentKind || '', positionalIndex);
      candidates = picked ? [picked] : [];
    } else {
      const selector = buildSelector(comp);
      console.log('scroll-to selector', selector, 'depth', depth);
      const matchedElements = collectMatches(scope, selector);
      candidates = depth === components.length - 1
        ? getInnermostMatches(matchedElements)
        : Array.from(matchedElements);
    }

    if (candidates.length === 0) {
      console.log('scroll-to no matched elements', 'depth', depth);
      return;
    }

    for (const candidate of candidates) {
      if (depth === components.length - 1) {
        if (candidates.length > 1) {
          console.log('scroll-to multiple matched elements at target depth', candidates);
        }
        console.log('scroll-to found element at target depth!', candidate, depth);
        finalTarget = candidate;
        found = true;
        return;
      }

      // Reveal this component's children via its kind's specific child-list expander(s), then
      // descend. We use a Timeout to let the browser catch up.
      expandChildLists(candidate, comp.kind);
      await new Promise(res => setTimeout(res, 100));

      // Recurse to the next depth level, passing this component's kind so a positional child can be
      // resolved within its child-list.
      await searchComponent(depth + 1, candidate, comp.kind);
      if (found) return;
    }
  }

  let found = false;
  try {
    await searchComponent(0, null, null);
    if (found) {
      return await scrollFinalTargetIntoView();
    }
  } catch (e) {
    captureException(new Error('scroll-to failed'), { extra: { cause: e } });
  }
  return false;
}

export default function ConfigSearch(props: {
  survey: ExpandedSurvey,
  close: () => void,
  editorRoot?: () => ShadowRoot | null,
}) {
  const [query, setQuery] = useState('')

  const [open, setOpen] = useState(true)

  const [results, setResults] = useState<TreeBlock[] | null>(null);
  const [resultTypes, setResultTypes] = useState<Record<MatchType, TreeBlock[]> | null>(null)
  const [selectedTypes, setSelectedTypes] = useState<Record<MatchType, boolean>>({ "Name/Title/Path": true, "Target Field": true, "Content": true, "Formula": true, "Kind": true, "Comments": true, "Other": true });
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({ matchCase: false, wholeMatchOnly: false, includeDashboards: true, includeComments: false, searchAllAttributes: false });
  const [searching, setSearching] = useState(false);
  const [showLength, setShowLength] = useState(0);
  const [navigationError, setNavigationError] = useState('');
  const [showThing, setShowThing] = useState<Record<string, boolean>>({}); // Used to hide large objects

  // Display labels in breadcrumbs while retaining IDs for navigation.
  const definitionLabelsById = useMemo(() => {
    const labels = new Map<string, string>();
    if (!props.survey?.definitions) return labels;
    for (const [id, definition] of Array.from(extractDefinitions(props.survey.definitions).entries())) {
      labels.set(id, readDefinition(definition).label);
    }
    return labels;
  }, [props.survey?.definitions]);

  const sourceDefinitions = useMemo(() => {
    const sources = new Map<string, TreeBlock>();
    for (const result of results || []) {
      const id = definitionIdOf(result.thing as SearchableComponent);
      if (id && result.location) sources.set(id, result);
    }
    return sources;
  }, [results]);

  const labelWaypoint = (waypoint: string) => {
    const prefix = 'Definition' + DELIMITER;
    if (!waypoint.startsWith(prefix)) return waypoint;
    const label = definitionLabelsById.get(waypoint.slice(prefix.length));
    return label ? prefix + label : waypoint;
  };

  async function navigate(result: TreeBlock, location = result.location) {
    try {
      setNavigationError('');
      if (location) {
        const root = props.editorRoot?.() ?? document.querySelector('distro-editor')?.shadowRoot;
        if (!root) throw new Error('The editor is not loaded.');
        await goToEditorLocation(root, location);
      } else {
        const roots = result.tab === 'definitions' ? (props.survey.definitions || []) : props.survey.survey;
        // Walk the editor the user is looking at: ConfigPage keeps one <distro-editor> per visited tab.
        const root = props.editorRoot?.() ?? document.querySelector('distro-editor')?.shadowRoot;
        const reached = await findAndGoToComponent(result.tree, roots, result.tab, root);
        if (!reached) {
          // Keep the dialog open so the user sees why nothing happened instead of it closing silently.
          setNavigationError("Couldn't find that spot in the open editor. Make sure you're on the tab that contains it, or search again to refresh the results.");
          return;
        }
      }
      props.close();
    } catch (error) {
      // goToEditorLocation says which step it stopped at and what the editor rendered instead;
      // that detail is the only way to tell a stale result from a location the editor draws
      // differently, so show it rather than only the advice.
      const detail = error instanceof Error ? error.message : String(error);
      console.log('[go-to-location] failed', { location, tab: result.tab, detail });
      setNavigationError(`This location changed or could not be opened. Search again to refresh the results. ${detail}`);
      captureException(error);
    }
  }

  async function doSearch() {
    // Using a callback here guarantees that multiple clicks will not cause multiple API calls
    // because React automatically queues the set requests and executes them sequentially.
    setSearching((currentSearching) => {
      if (currentSearching) {
        console.log("Already clicked");
        return currentSearching;
      }
      (async () => {
        if (!query || query.length < 2) {
          setResults(null);
          setResultTypes(null);
          return;
        }

        console.time('findComponent');
        const components = await findComponent(props.survey, query, searchOptions);
        console.timeEnd('findComponent');

        // Group components by type for easy toggling
        const componentTypes = (components || []).reduce<Record<MatchType, TreeBlock[]>>(
          (acc, cur) => {
            for (let matchType of cur.matchTypes) {
              acc[matchType] ||= [];
              acc[matchType].push(cur);
            }
            return acc;
          }, { "Name/Title/Path": [], "Target Field": [], "Content": [], "Formula": [], "Kind": [], "Comments": [], "Other": [] });

        setResults(components || null);
        setShowLength((components?.length || 0) > 20 ? 20 : components?.length || 0);
        setResultTypes(componentTypes);
        setSelectedTypes((Object.keys(componentTypes) as Array<keyof typeof componentTypes>).reduce<Record<MatchType, boolean>>((acc, cur) => {
          acc[cur] = selectedTypes[cur];
          return acc;
        }, { "Name/Title/Path": false, "Target Field": false, "Content": false, "Formula": false, "Kind": false, "Comments": false, "Other": false }));
        setSearching(false);
      })();

      return true;
    });
  }

  useEffect(() => {
    if (!query || query.length < 2) return;

    const timer = setTimeout(() => {
      doSearch();
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [query, searchOptions, props.survey]);


  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => {
      setQuery('');
      props.close();
    }} appear>
      <Dialog as="div" className="relative mt-20" style={{ zIndex: 100 }} onClose={() => {
        setOpen(false);
        props.close();
      }}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mt-20 mx-auto max-w-4xl transform divide-y bg-white divide-gray-100 overflow-hidden rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
              <>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    {/** svg magnifying glass */}
                    <svg className="svg-icon search-icon" aria-labelledby="title desc" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.9 19.7">
                      <title id="title">Search Icon</title>
                      <desc id="desc">A magnifying glass icon.</desc>
                      <g className="search-path" fill="none" stroke="#848F91">
                        <path strokeLinecap="square" d="M18.5 18.3l-5.4-5.4" />
                        <circle cx="8" cy="8" r="7" />
                      </g>
                    </svg>
                  </div>
                  <input
                    className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm"
                    placeholder="Search..."
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>

                {(query?.length < 2) && (
                  <div className="py-14 px-6 text-center text-sm sm:px-14">
                    <p className="mt-4 font-semibold text-gray-900">Search term must be 2 or more characters</p>
                  </div>
                )}

                {searching ? (
                  <div className="p-6 text-center text-sm sm:px-14">
                    <p className="mt-4 font-semibold text-gray-900">Searching...</p>
                  </div>
                ) : (
                  (query?.length >= 2) && (
                    <div className="px-6 pt-2 text-center text-sm sm:px-14">
                      <h3>Search Options</h3>
                      <div className="mt-4 grid grid-cols-2 gap-y-2 mb-2 justify-items-start items-start">
                        <Switch
                          checked={searchOptions.matchCase}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, matchCase: !searchOptions.matchCase }))}
                          label={'Match Case'} />
                        <Switch
                          checked={searchOptions.wholeMatchOnly}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, wholeMatchOnly: !searchOptions.wholeMatchOnly }))}
                          label={'Whole Match Only'} />
                        <Switch
                          checked={searchOptions.includeDashboards}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, includeDashboards: !searchOptions.includeDashboards }))}
                          label={'Include Dashboards'} />
                        <Switch
                          checked={searchOptions.includeComments}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, includeComments: !searchOptions.includeComments }))}
                          label={'Include Comments'} />
                        <Switch
                          checked={searchOptions.searchAllAttributes}
                          onCheckedChange={() => setSearchOptions(prev => ({ ...prev, searchAllAttributes: !searchOptions.searchAllAttributes }))}
                          label={'Include Everything'} />
                        {resultTypes && (Object.keys(resultTypes) as Array<keyof typeof resultTypes>).map(t => {
                          return <div key={t}>
                            <Switch key={'switch-' + t}
                              checked={selectedTypes[t]}
                              onCheckedChange={() => setSelectedTypes(prev => ({ ...prev, [t]: !prev[t] }))}
                              label={`${t}: ${resultTypes[t].length}`} />
                          </div>
                        })}
                      </div>
                      <h3>Search results</h3>
                      {navigationError && <p role="alert" className="text-red-700">{navigationError}</p>}
                      {results?.length ? (
                        <div>
                          <p className="mt-2 text-gray-500">
                            Found {results.length} results for {query}
                          </p>
                          <p className="mt-0 text-gray-400">
                            {/* TODO: we currently show the same result multiple times if it has mutiple result types,
                            for example, if the search term appears in both the targetfield and content for a component
                            this may or may not be desirable, but it can cause this count to say things like: "Showing 12 of 8 results" */}
                            (Showing {(Object.keys(selectedTypes || {}) as Array<keyof typeof resultTypes>).reduce((acc, cur) => {
                              const resultTypeLength = selectedTypes[cur] ? Object.keys(resultTypes?.[cur] || {}).length : 0;
                              return acc + (resultTypeLength < showLength ? resultTypeLength : showLength);
                            }, 0)} of {results.length} results)
                          </p>
                          <div className="mt-6">
                            {resultTypes && (Object.keys(resultTypes) as Array<keyof typeof resultTypes>).map((t, tIdx) => {
                              return <ul className="mt-6" hidden={!selectedTypes[t]} key={t}>
                                {resultTypes[t] && Object.values(resultTypes[t]).slice(0, showLength).map((result, idx) => {
                                  const content = JSON.stringify(result.thing, null, 2);
                                  const evidence = result.definitionMatches || [];
                                  const previewLength = content.length + evidence.reduce((length, match) => length + match.text.length, 0);
                                  const uuid = content.replace(/[^a-zA-Z0-9]/g, '') + idx + t + tIdx;
                                  
                                  return (
                                    <li key={uuid} className="mt-4 p-4 text-left border-2 border-b-2 border-black">
                                      {result.tab && result.tab !== 'survey' && (
                                        <span className="inline-block mb-2 px-2 py-1 text-xs font-semibold text-white bg-blue-600 rounded">
                                          {result.tab}
                                        </span>
                                      )}
                                      <span className="inline-flex flex-wrap items-center">{result.tree.map((v, index) => <>
                                        <button
                                          className={classNames(index === (result.tree.length - 1) ? "font-semibold bg-green-300 rounded p-1 hover:animate-pulse" : "font-semibold")}
                                          disabled={index !== (result.tree.length - 1)}
                                          onClick={() => navigate(result)}>
                                          {labelWaypoint(v)}
                                        </button>
                                        &nbsp;{index !== (result.tree.length - 1) && <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />}&nbsp;
                                      </>)}</span>
                                      {/** This can get really long so if it's really long let's collapse by default */}
                                      {previewLength > 500 && <button onClick={() => setShowThing(prev => ({ ...prev, [uuid]: !prev[uuid] }))} className="float-right px-2 py-1 bg-gray-200 hover:bg-gray-300">{showThing[uuid] ? 'Hide' : 'Show'}</button>}
                                      <pre className={classNames("bg-gray-50 my-3 p-2", (previewLength > 500 && !showThing[uuid]) ? 'hidden' : '')} style={{ whiteSpace: 'pre-wrap' }}>
                                        {highlightSearchTerm(content, query, searchOptions)}
                                      </pre>
                                      {result.location && <div className="flex gap-3 text-sm">
                                        <button className="underline" onClick={() => navigate(result)}>
                                          {isDefinitionReference(result.thing) ? 'Go to Reference' : 'Go to Definition'}
                                        </button>
                                        {result.definitionLocation && <button className="underline" onClick={() => navigate(result, result.definitionLocation)}>
                                          Go to Definition: {result.definition && readDefinition(result.definition).label}
                                        </button>}
                                      </div>}
                                      {result.invalidReference && <p className="text-amber-700 text-sm">This Reference points to a missing or incompatible Definition.</p>}
                                      {evidence.length > 0 && <div className={classNames('bg-gray-50 my-2 p-2', previewLength > 500 && !showThing[uuid] ? 'hidden' : '')}>
                                        <div className="text-xs text-gray-500">Matching Definition text:</div>
                                        {evidence.map((match, i) => <div key={i} className="my-1 text-sm">
                                          <span className="font-semibold">{definitionLabelsById.get(match.definitionId) || match.definitionId} · {match.matchType}: </span>
                                          {highlightSearchTerm(match.text, query, searchOptions)}
                                          {sourceDefinitions.get(match.definitionId) &&
                                            <button className="underline ml-2" onClick={() => navigate(sourceDefinitions.get(match.definitionId)!)}>Go to source Definition</button>}
                                        </div>)}
                                      </div>}
                                    </li>
                                  )
                                })}
                              </ul>
                            })}
                            {results && (showLength < results.length) && (
                              <div className="m-4 text-center">
                                <button className={BUTTON_CLASS} onClick={() => setShowLength(showLength + 20)}>Show More</button>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="py-14 px-6 text-center text-sm sm:px-14">
                          <p className="mt-4 font-semibold text-gray-900">No results found</p>
                          <p className="mt-2 text-gray-500">
                            We couldnt find anything with that term. Please try again.
                          </p>
                        </div>
                      )}
                    </div>
                  )
                )}
              </>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export type FieldMismatchMap = Record<string, { tree: string[], text: Text, fieldsInLangMap: Record<keyof Text, string[]> }>;

/**
 *  Returns a FieldMismatchMap to be used for scrolling to the component
 *  containing a field mismatch via findAndGoToComponent in ConfigSearch.tsx
 */
export function buildFieldMismatchPathMap(
  components: SearchableComponent[],
  map: FieldMismatchMap = {}
): FieldMismatchMap {
  const searchForFieldMismatches = (node: any, outerComponent: SearchableComponent, tree: string[], map: Record<string, { tree: string[], text: Text, fieldsInLangMap: Record<keyof Text, string[]> }>) => {
    if (typeof node === 'object' && typeof node.en === 'string' && Object.keys(node).length > 1) {
  
      // Create mapping of language code to array of target fields contained in the text for that language
      const fieldsInLangMap: Record<string, string[]> = {};
      Object.entries(node).forEach(([lang, content]) => {
        if (lang === '_id' || lang === '_trackedObjInfo' || lang === 'kind' || typeof content !== 'string') return;
        fieldsInLangMap[lang] = (content.match(FIELD_REF_REGEX) || []);
        if (fieldsInLangMap[lang].length === 0) {
          fieldsInLangMap[lang] = ['[Empty]'];
        }
      });
  
      // For each language that is not English, compare included fields to those in English, 
      let fieldsMatch = true;
      outer: for (const lang of Object.keys(fieldsInLangMap)) {
        if (lang === 'en') continue;
        if (fieldsInLangMap[lang]?.length !== fieldsInLangMap['en']?.length) {
          fieldsMatch = false;
          break;
        }
        for (let i = 0; i < fieldsInLangMap[lang].length; i++) {
          if (!(fieldsInLangMap['en'] || []).includes(fieldsInLangMap[lang][i])) {
            fieldsMatch = false;
            break outer;
          }
        }
      }
  
      if (!fieldsMatch) {
        map[outerComponent.kind + DELIMITER + (('targetField' in outerComponent && outerComponent.targetField) ? outerComponent.targetField : generateUuid(outerComponent))] = { tree, text: node, fieldsInLangMap };
      }
      return;
    }
  
    if (typeof node === 'object') {
      if (Array.isArray(node)) {
        node.flatMap((subNode) => searchForFieldMismatches(subNode, outerComponent, tree, map));
        return;
      }
  
      Object.values(node).flatMap((subNode) => searchForFieldMismatches(subNode, outerComponent, tree, map));
      return;
    }
    return;
  }

  return buildPathMap({
    components,
    map,
    handlePotentialTarget: (component, tree, map) => {
      searchForFieldMismatches(component, component, tree, map);
    },
  });
}

/**
 *  Used to build a tree map function that can be used to find components.
 *  The function recurses through container components like Collections, Sections, Subsurveys, and Conditional Blocks,
 *  and when it finds a non-container component, it calls the handlePotentialTarget function.
 * 
 *  The handlePotentialTarget function should set the map using whatever key makes sense for the need,
 *  with the value including the tree and whatever other data is required from the component.
 */
export function buildPathMap<T>({ components, map, handlePotentialTarget }: {
  components: SearchableComponent[],
  map: T,
  handlePotentialTarget: (component: SearchableComponent, tree: string[], map: T) => void;
}): T {
  function buildPathInner(components: SearchableComponent[], parentTree: string[]) {
    components.forEach((component, i) => {
      // Same waypoint encoding as findComponent, so trees built here resolve identically in
      // getComponentsInPath: Templated Blocks via their id, identifier-less nodes (Conditional
      // Blocks) positionally by index within their parent's child-list.
      const tree = [...parentTree, encodeWaypoint(component, i)];

      const children = getChildComponents(component);
      if (children) {
        buildPathInner(children, tree);
      } else {
        handlePotentialTarget(component, tree, map);
      }
    });
  }
  buildPathInner(components, []);
  return map;
}

export const TEMPLATED_COMPONENT_KEY_DELIMITER = '::';

/** Composite key that ties an expanded row back to the single un-expanded component it came from. */
export function templatedComponentKey(blockId: string, preSubstitutionIdentifier: string): string {
  return blockId + TEMPLATED_COMPONENT_KEY_DELIMITER + preSubstitutionIdentifier;
}

/**
 *  Maps each component that lives inside a Templated Block to its scroll-to
 *  path, built over the UN-EXPANDED survey. This lets the Notifications/Payments tabs, and the
 *  Navigation preview's pills — whose rows/nodes come from the EXPANDED survey —
 *  recover the path to the single un-expanded component the editor renders for a templated
 *  row/node, by looking it up with templatedComponentKey(_parentTemplatedBlockId,
 *  _preSubstitutionIdentifier).
 *
 *  The key is scoped by the (innermost) block id so a same-named component elsewhere in the survey
 *  can't collide. If two components in the SAME block still share an identifier, that key is dropped
 *  as ambiguous — callers then leave the row's original path alone rather than risk a wrong target.
 *  Only templated rows/nodes should be looked up here; non-templated ones already have precise paths.
 *  Pass the same un-expanded survey handed to findAndGoToComponent.
 */
export function buildTemplatedComponentPathMap(components: SearchableComponent[]): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  const ambiguous = new Set<string>();

  const record = (component: SearchableComponent, tree: string[], blockId: string) => {
    const identifier = getComponentIdentifier(component);
    if (!identifier) return;
    // Containers sometimes share a name/title with one of its children,
    // and an unqualified identifier would collide between the two.
    const key = templatedComponentKey(blockId, component.kind + DELIMITER + identifier);
    if (key in map) ambiguous.add(key);
    map[key] = tree;
  };

  const walk = (comps: SearchableComponent[], parentTree: string[], blockId: string | undefined) => {
    comps.forEach((component, i) => {
      const tree = [...parentTree, encodeWaypoint(component, i)];
      // Templated Block wrappers themselves are never tagged with `_parentTemplatedBlockId` so
      // nothing ever looks one up here; recording it would only add noise and risk of collision.
      if (blockId && component.kind !== 'Templated Block') record(component, tree, blockId);
      // Descend carrying the innermost enclosing block id (matches the `_parentTemplatedBlockId`
      // that expansion stamps onto components, which is likewise the innermost block).
      const childBlockId = component.kind === 'Templated Block' && (component as any).id
        ? (component as any).id
        : blockId;
      const children = getChildComponents(component);
      if (children) {
        walk(children, tree, childBlockId);
      }
    });
  };

  walk(components, [], undefined);
  for (const key of Array.from(ambiguous)) delete map[key];
  return map;
}
